import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from '../../../redux/modules/store';
import { Route, Switch } from 'react-router-dom';
import ContentHeader from '../../organisms/ContentHeader';
import VisitSummary from '../../organisms/VisitSummary';
import VisitList from '../../organisms/VisitList';
import { REFRESH_BTN_COMMON, SCREEN, getCompanyCfcDisplayName, isPastDay } from '../../../Common';
import VisitTabs from '../../molecules/VisitTabs';
import HeaderIcon from '../../atoms/HeaderIcon';
import LoadingArea from '../../organisms/LoadingArea';
import { LoadingStatus } from '../../../redux/reducers/LoadingStatusReducer';
import { VisitReservationInfoState, VisitReservationStoreInfo } from '../../../redux/reducers/VisitReservationInfoReducer';
import { MasterInfoState } from '../../../redux/reducers/MasterInfoReducer';
import SelectStoreName from '../../molecules/SelectStoreName';
import VisitStoreSelectModal from '../VisitStoreSelectModal';
import { GASpotEventState } from '../NegotiationCountList';

type VisitDashboardProps = {
  path: string;
  isShowVisitSelectStoreModal: boolean;
  visitReservationInfo: VisitReservationInfoState;
  masterInfo: MasterInfoState;
  loadingStatus: LoadingStatus;
  targetStore: string;
  targetDate: Date;
  inquiryDate: Date;
  updateTargetStore?: (targetStoreCd: string) => void;
  updateTargetDate: (targetDate: Date) => void;
  defaultStoreName?: string;
  gaSpotEvent: {
    [key: string]: GASpotEventState | { [key: string]: GASpotEventState };
  };
};

const VisitDashboard: React.FC<VisitDashboardProps> = ({
  path,
  isShowVisitSelectStoreModal,
  visitReservationInfo,
  masterInfo,
  loadingStatus,
  targetStore,
  targetDate,
  inquiryDate,
  updateTargetStore,
  updateTargetDate,
  defaultStoreName,
  gaSpotEvent,
}) => {
  const [activeTabType, setActiveTabType] = useState(
    SCREEN.VISIT.TAB_LIST.find((tab) => tab.url === path)?.type ?? SCREEN.VISIT.TAB_LIST[0].type,
  );
  const [isOpenStoreSelectModal, setIsOpenStoreSelectModal] = useState(false);
  const [selectedStoreName, setSelectedStoreName] = useState('店舗を選択してください');

  const fetchVisitReservationStoreListLoadingStatus = useSelector((state: AppState) => state.loadingStatus.fetchVisitReservationStoreList);

  const isLoading = loadingStatus.isLoading || fetchVisitReservationStoreListLoadingStatus.isLoading;
  useEffect(() => {
    if (defaultStoreName) {
      setSelectedStoreName(defaultStoreName);
    }
  }, [defaultStoreName]);

  /**
   * 検索対象の店舗を保持する
   * @param targetStore 選択した店舗コード
   */
  const updateTargetStoreInfo = (targetStores: VisitReservationStoreInfo[]) => {
    updateTargetStore && updateTargetStore(targetStores[0].chkdegtOrdcstmCd);
    setSelectedStoreName(getCompanyCfcDisplayName(targetStores[0].companyCfc, targetStores[0].longNm));
    setIsOpenStoreSelectModal(false);
  };

  return (
    <div className='layout-tab'>
      <div className='layout-tab__header u-bg-white'>
        <div className='container u-pb0'>
          <ContentHeader
            screenId={SCREEN.VISIT.ID}
            screenName={SCREEN.VISIT.NAME}
            statusType=''
            loadingStatus={loadingStatus}
            gaSpotEvent={gaSpotEvent[REFRESH_BTN_COMMON.KEY] as GASpotEventState}
            titleIcon={
              <HeaderIcon
                iconClassName='dashboard-summary-icon'
                icon='store'
                className='dashboard-summary-icon-wrap dashboard-summary-icon-wrap--small dashboard-summary-icon-wrap--mail u-float-left u-mr8'></HeaderIcon>
            }
            data-testid='visit-dashboard--content-header'>
            {isShowVisitSelectStoreModal && (
              <>
                <SelectStoreName
                  selectedStoreName={selectedStoreName}
                  onclick={() => {
                    setIsOpenStoreSelectModal(true);
                  }}></SelectStoreName>
                <VisitStoreSelectModal
                  isOpen={isOpenStoreSelectModal}
                  onClose={() => {
                    setIsOpenStoreSelectModal(false);
                  }}
                  storeList={visitReservationInfo.storeList}
                  updateTargetStore={updateTargetStoreInfo}
                />
              </>
            )}
          </ContentHeader>
        </div>
        <VisitTabs
          activeTabKey={activeTabType}
          tabList={SCREEN.VISIT.TAB_LIST}
          data-testid='visit-dashboard--tabs'
          onTabClick={(type) => {
            setActiveTabType(type);
          }}
          gaSpotEventTabs={gaSpotEvent.visitTab as { [key: string]: GASpotEventState }}
        />
      </div>
      {isLoading ? (
        <LoadingArea />
      ) : (
        <div className='layout-tab__content'>
          <Switch>
            <Route
              path={SCREEN.VISIT.TAB_LIST[0].url}
              exact
              component={() => (
                <VisitSummary
                  visitReservationSummaryInfo={visitReservationInfo.summaryInfo}
                  availableStoreCnt={visitReservationInfo.storeList.length}
                  masterInfo={masterInfo}
                  targetStore={targetStore}
                  targetDate={targetDate}
                  inquiryDate={inquiryDate}
                  isPastDay={isPastDay(targetDate, inquiryDate)}
                  updateTargetDate={updateTargetDate}
                  isShowVisitSelectStoreModal={isShowVisitSelectStoreModal}
                />
              )}
            />
            <Route
              path={SCREEN.VISIT.TAB_LIST[1].url}
              exact
              component={() => (
                <VisitList
                  gaSpotEvent={gaSpotEvent.infoNumber as GASpotEventState}
                  visitReservationListInfo={visitReservationInfo.listInfo}
                  availableStoreCnt={visitReservationInfo.storeList.length}
                  masterInfo={masterInfo}
                  targetStore={targetStore}
                  targetDate={targetDate}
                  inquiryDate={inquiryDate}
                  isPastDay={isPastDay(targetDate, inquiryDate)}
                  updateTargetDate={updateTargetDate}
                  isShowVisitSelectStoreModal={isShowVisitSelectStoreModal}
                />
              )}
            />
          </Switch>
        </div>
      )}
    </div>
  );
};

export default VisitDashboard;
